import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { LateralJobPosition } from "@/types/database";

const TIMEZONES: { value: string; label: string }[] = [
  { value: "America/New_York", label: "US Eastern (America/New_York)" },
  { value: "America/Chicago", label: "US Central (America/Chicago)" },
  { value: "America/Los_Angeles", label: "US Pacific (America/Los_Angeles)" },
  { value: "Europe/Berlin", label: "Central European (Europe/Berlin)" },
];

const emptyForm = (): Partial<LateralJobPosition> => ({
  job_title: "",
  job_code: "",
  job_description: "",
  ai_evaluation_prompt: "",
  is_active: true,
  is_remote: false,
  work_timezone: null,
});

export default function LateralJobPositionsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LateralJobPosition | null>(null);
  const [form, setForm] = useState<Partial<LateralJobPosition>>(emptyForm());

  const { data: positions, isLoading } = useQuery({
    queryKey: ["lateral-job-positions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lateral_job_positions")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as LateralJobPosition[];
    },
  });

  const upsert = useMutation({
    mutationFn: async (values: Partial<LateralJobPosition>) => {
      if (editing) {
        const { error } = await supabase
          .from("lateral_job_positions")
          .update({ ...values, updated_at: new Date().toISOString() })
          .eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("lateral_job_positions")
          .insert(values as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lateral-job-positions"] });
      toast({ title: editing ? "Position updated" : "Position created" });
      closeDialog();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("lateral_job_positions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lateral-job-positions"] });
      toast({ title: "Position deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (pos: LateralJobPosition) => {
    setEditing(pos);
    setForm({
      job_title: pos.job_title,
      job_code: pos.job_code ?? "",
      job_description: pos.job_description ?? "",
      ai_evaluation_prompt: pos.ai_evaluation_prompt ?? "",
      is_active: pos.is_active,
      is_remote: pos.is_remote,
      work_timezone: pos.work_timezone ?? null,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.job_title?.trim()) return;
    upsert.mutate(form);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lateral Job Positions</h1>
          <p className="text-sm text-gray-500 mt-1">Define roles for experienced hires</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Position
        </Button>
      </div>

      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Remote</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  Loading...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !positions?.length && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  No positions yet. Create one to get started.
                </TableCell>
              </TableRow>
            )}
            {positions?.map((pos) => (
              <TableRow key={pos.id}>
                <TableCell className="font-medium">{pos.job_title}</TableCell>
                <TableCell className="text-gray-500">{pos.job_code || "-"}</TableCell>
                <TableCell>
                  {pos.is_remote ? (
                    <div className="flex flex-col gap-0.5">
                      <Badge variant="outline" className="w-fit">Remote</Badge>
                      {pos.work_timezone && (
                        <span className="text-xs text-gray-500">{pos.work_timezone}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">On-site</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={pos.is_active ? "default" : "secondary"}>
                    {pos.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(pos)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => remove.mutate(pos.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Job Position" : "New Job Position"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="job_title">Job Title *</Label>
                <Input
                  id="job_title"
                  value={form.job_title ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                  placeholder="e.g. Senior Backend Engineer"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="job_code">Job Code</Label>
                <Input
                  id="job_code"
                  value={form.job_code ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, job_code: e.target.value }))}
                  placeholder="e.g. SBE-001"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="job_description">Job Description</Label>
              <Textarea
                id="job_description"
                value={form.job_description ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, job_description: e.target.value }))}
                placeholder="Describe the role, responsibilities, and requirements..."
                rows={4}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ai_eval_prompt">AI Evaluation Prompt</Label>
              <Textarea
                id="ai_eval_prompt"
                value={form.ai_evaluation_prompt ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, ai_evaluation_prompt: e.target.value }))}
                placeholder="Additional criteria for the AI to use when evaluating CVs for this role..."
                rows={4}
              />
              <p className="text-xs text-gray-500">
                This will be appended to the base evaluation prompt when screening CVs.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="is_remote"
                checked={form.is_remote ?? false}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, is_remote: v, work_timezone: v ? f.work_timezone : null }))
                }
              />
              <Label htmlFor="is_remote">Remote position</Label>
            </div>
            {form.is_remote && (
              <div className="space-y-1.5">
                <Label htmlFor="work_timezone">Working Timezone</Label>
                <Select
                  value={form.work_timezone ?? ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, work_timezone: v || null }))}
                >
                  <SelectTrigger id="work_timezone">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={form.is_active ?? true}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? "Saving..." : editing ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
